
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ComparisonResult, SummaryDifference } from '@/types/quick-compare';
import { Badge } from '@/components/ui/badge';
import { Plus, Minus, FileText, AlertCircle } from 'lucide-react';
import { formatCurrency } from '@/utils/estimates/formatCurrency';

interface SummaryTabProps {
  results: ComparisonResult;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({ results }) => {
  const { summary, original, modified, summaryDifferences = [] } = results;
  
  const formattedPrice = formatCurrency(Math.abs(summary.priceDifference));
  
  const priceChange = summary.priceDifference > 0 
    ? `Increase of ${formattedPrice}`
    : (summary.priceDifference < 0 
      ? `Decrease of ${formattedPrice}` 
      : 'No change in price');

  // Helper function to format camelCase field names to Title Case With Spaces
  const formatFieldName = (fieldName: string): string => {
    // First replace camelCase with spaces (before each capital letter)
    const withSpaces = fieldName.replace(/([A-Z])/g, ' $1');
    // Then capitalize the first letter and return
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  // Format hours to display with one decimal place
  const formatHours = (hours: number): string => {
    return hours.toFixed(1);
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Price Difference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <span className={`text-2xl font-bold ${
                summary.priceDifference < 0 
                  ? 'text-red-600' 
                  : (summary.priceDifference > 0 
                    ? 'text-green-600' 
                    : 'text-gray-600')
              }`}>
                {summary.priceDifference > 0 && '+'}
                {formattedPrice}
              </span>
            </div>
            <p className="text-muted-foreground text-sm mt-1">{priceChange}</p>
          </CardContent>
        </Card>
        
        <ChangeOverviewCard summary={summary} />
      </div>
      
      {summaryDifferences.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Summary Differences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {['Totals', 'Labor Hours', 'Labor', 'Parts', 'Materials', 'Calculated Totals'].map(category => {
              const categoryDiffs = summaryDifferences.filter(diff => diff.category === category);
              if (categoryDiffs.length === 0) return null;
              
              return (
                <div key={category} className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">{category}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {categoryDiffs.map((diff, idx) => (
                      <SummaryDifferenceItem 
                        key={idx} 
                        difference={diff} 
                        formatFieldName={formatFieldName} 
                        category={category} 
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Estimate Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <EstimateInfoBlock title="Original Estimate" estimate={original} />
            <EstimateInfoBlock title="Modified Estimate" estimate={modified} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component for summary differences
const SummaryDifferenceItem = ({ 
  difference,
  formatFieldName,
  category
}: { 
  difference: SummaryDifference,
  formatFieldName: (fieldName: string) => string,
  category: string
}) => {
  const isIncrease = difference.difference > 0;
  const absValue = Math.abs(difference.difference);
  
  // Format values differently based on category
  const formatValue = (value: number, category: string) => {
    if (category === 'Labor Hours') {
      return value.toFixed(1); // Format hours with one decimal place
    } else {
      return formatCurrency(value); // Use currency formatting for monetary values
    }
  };

  // Fixed badge colors - negative values should be red, positive values should be green
  return (
    <div className="border rounded-md p-3 bg-muted/20">
      <div className="flex justify-between items-center">
        <span className="text-sm">{formatFieldName(difference.field)}</span>
        <Badge variant="outline" className={isIncrease ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}>
          {isIncrease ? '+' : '-'} {formatValue(absValue, category)}
        </Badge>
      </div>
      <div className="flex justify-between mt-1 text-xs text-muted-foreground">
        <span>Original: {formatValue(difference.originalValue, category)}</span>
        <span>New: {formatValue(difference.newValue, category)}</span>
      </div>
    </div>
  );
}

// Helper component for change overview
const ChangeOverviewCard = ({ summary }: { summary: ComparisonResult['summary'] }) => {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Changes Overview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <ChangeStatItem 
          type="added" 
          count={summary.addedItems} 
          label="Added" 
        />
        <ChangeStatItem 
          type="removed" 
          count={summary.removedItems} 
          label="Removed" 
        />
        <ChangeStatItem 
          type="changed" 
          count={summary.changedItems} 
          label="Changed" 
        />
      </CardContent>
    </Card>
  );
};

// Helper component for change statistics
const ChangeStatItem = ({ 
  type, 
  count, 
  label 
}: { 
  type: 'added' | 'removed' | 'changed',
  count: number, 
  label: string 
}) => {
  const icons = {
    added: <Plus className="h-3 w-3 mr-1" />,
    removed: <Minus className="h-3 w-3 mr-1" />,
    changed: <FileText className="h-3 w-3 mr-1" />
  };

  const colors = {
    added: "bg-green-50 text-green-700 border-green-200",
    removed: "bg-red-50 text-red-700 border-red-200",
    changed: "bg-amber-50 text-amber-700 border-amber-200"
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={colors[type]}>
          {icons[type]} {label}
        </Badge>
        <span>{count} items</span>
      </div>
    </div>
  );
};

// Helper component for estimate info
const EstimateInfoBlock = ({ 
  title, 
  estimate 
}: { 
  title: string, 
  estimate: ComparisonResult['original'] | ComparisonResult['modified'] 
}) => {
  // Calculate total labor hours with proper formatting
  const calculateTotalHours = (estimate: any): string => {
    if (!estimate.summary) return '0.0';
    
    const bodyHours = estimate.summary.labor?.bodyHours || 0;
    const refinishHours = estimate.summary.labor?.refinishHours || 0;
    const mechanicalHours = estimate.summary.labor?.mechanicalHours || 0;
    
    const total = bodyHours + refinishHours + mechanicalHours;
    return total.toFixed(1); // Always display with one decimal point
  };

  return (
    <div className="space-y-2">
      <h3 className="font-medium">{title}</h3>
      <p className="text-sm text-muted-foreground">Document: {estimate.fileName}</p>
      <p className="text-sm text-muted-foreground">VIN: {estimate.vehicleInfo?.vin}</p>
      {estimate.metadata?.roNumber && (
        <p className="text-sm text-muted-foreground">RO #: {estimate.metadata.roNumber}</p>
      )}
      {estimate.summary ? (
        <>
          <p className="text-sm text-muted-foreground">
            Subtotal: {formatCurrency(estimate.summary.subtotal)}
          </p>
          <p className="text-sm text-muted-foreground">
            Sales Tax: {formatCurrency(estimate.summary.salesTax)}
          </p>
          <p className="text-sm text-muted-foreground">
            Labor Hours: {calculateTotalHours(estimate)}
          </p>
          {estimate.summary.labor?.bodyHours > 0 && (
            <p className="text-xs text-muted-foreground ml-4">
              Body: {estimate.summary.labor.bodyHours.toFixed(1)}
            </p>
          )}
          {estimate.summary.labor?.refinishHours > 0 && (
            <p className="text-xs text-muted-foreground ml-4">
              Refinish: {estimate.summary.labor.refinishHours.toFixed(1)}
            </p>
          )}
          {estimate.summary.labor?.mechanicalHours > 0 && (
            <p className="text-xs text-muted-foreground ml-4">
              Mechanical: {estimate.summary.labor.mechanicalHours.toFixed(1)}
            </p>
          )}
          <p className="text-sm font-medium">
            Grand Total: {formatCurrency(estimate.summary.grandTotal)}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Total: {formatCurrency(estimate.totalAmount)}
        </p>
      )}
    </div>
  );
};
